import { render } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import { applySavedTheme, get, getTheme, html, IN_APP, linkHandler, MARKETING_URL, navigate, onNavigate, post, setTheme } from "./lib.js";
import { LangToggle, Loading, Logo, Toaster, useLoad, usePoll } from "./ui.js";
import { applyLang, getLang, tr } from "./i18n.js";
import { AuthPage, ResetPage } from "./pages/auth.js";
import { OverviewPage } from "./pages/overview.js";
import { InboxPage } from "./pages/inbox.js";
import { EmailPage } from "./pages/email.js";
import { CallsPage } from "./pages/calls.js";
import { BookingsPage } from "./pages/bookings.js";
import { ContactsPage } from "./pages/contacts.js";
import { TasksPage } from "./pages/tasks.js";
import { ServicesPage } from "./pages/services.js";
import { AgentPage } from "./pages/agent.js";
import { ChannelsPage } from "./pages/channels.js";
import { CampaignsPage } from "./pages/campaigns.js";
import { SettingsPage } from "./pages/settings.js";
import { AdminPage } from "./pages/admin.js";
import { AcademyPage } from "./pages/academy.js";
import { BillingBanner } from "./pages/billing.js";
import { JoinPage } from "./pages/join.js";
import { SetupPage } from "./pages/setup.js";
import { IntakePage } from "./pages/intake.js";
import { WebsitePage } from "./pages/website.js";
import { AssistantDrawer, pendingCount, Sparkles, useAssistant } from "./assistant.js";

// A business website's host (bready.relayza.company) serves its site at "/" only; static hosting
// answers any other path with this dashboard before the Worker runs, so send the visitor home.
const SITE_HOST = /^(?!app\.|www\.)[a-z0-9-]+\.relayza\.company$/i.test(location.hostname);
if (SITE_HOST && location.pathname !== "/") location.replace("/");

const NAV = [
  { path: "/", label: "Overview", icon: "◧" },
  { path: "/inbox", label: "Inbox", icon: "✉", badge: (a) => a.needs_person },
  { path: "/email", label: "Email", icon: "@", badge: (a) => a.email_todo ?? 0 },
  { path: "/calls", label: "Call notes", icon: "☏" },
  { path: "/tasks", label: "Tasks", icon: "☑", badge: (a) => a.overdue_tasks + a.due_today_tasks },
  { path: "/bookings", label: "Bookings", icon: "▦" },
  { path: "/contacts", label: "Contacts", icon: "☺" },
  { path: "/services", label: "Services & staff", icon: "✂" },
  { path: "/agent", label: "AI agent", icon: "✦" },
  { path: "/channels", label: "Channels", icon: "⇄" },
  { path: "/campaigns", label: "Campaigns", icon: "➚" },
  { path: "/intake", label: "Intake forms", icon: "▤" },
  { path: "/website", label: "Website", icon: "◎" },
  { path: "/settings", label: "Settings", icon: "⚙" },
];

function usePath() {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => onNavigate(() => setPath(location.pathname)), []);
  return path;
}

applySavedTheme();
applyLang();

const TENANT_KEY = "mz.tenant";

// Full-height layout reads the window's real height, not 100vh, which Android's WebView can get
// badly wrong (the menu drawer came out a sliver tall there, its links out of reach).
const setAppHeight = () => document.documentElement.style.setProperty("--app-h", `${window.innerHeight}px`);
setAppHeight();
window.addEventListener("resize", setAppHeight);

function App() {
  const path = usePath();
  const [me, setMe] = useState(undefined);
  const [tenantId, setTenantId] = useState(() => {
    // The app opens the full dashboard on the business it has selected (?tenant=…); the
    // parameter is dropped from the address once read, and the usual check below still
    // falls back to the first business if this account can't reach it.
    const fromUrl = new URLSearchParams(location.search).get("tenant");
    if (fromUrl) {
      const url = new URL(location.href);
      url.searchParams.delete("tenant");
      history.replaceState(null, "", url.pathname + url.search + url.hash);
      return fromUrl;
    }
    try {
      return localStorage.getItem(TENANT_KEY);
    } catch {
      return null;
    }
  });
  const [tenant, setTenant] = useState(null);
  const [navOpen, setNavOpen] = useState(false);

  const loadMe = () =>
    get("/auth/me")
      .then(setMe)
      .catch(() => setMe(null));
  useEffect(() => {
    loadMe();
  }, []);

  // Pick a tenant the user can access.
  useEffect(() => {
    if (!me) return;
    const ids = me.tenants.map((t) => t.id);
    if (!tenantId || !ids.includes(tenantId)) setTenantId(ids[0] ?? null);
  }, [me]);

  // The Academy lives under RELAYZA's own business: opening it selects that business when this
  // account can reach it.
  useEffect(() => {
    const home = me?.academy && me.academy_tenant_id;
    if (home && path.startsWith("/academy") && tenantId !== home && me.tenants.some((t) => t.id === home)) setTenantId(home);
  }, [me, path]);

  useEffect(() => {
    if (!tenantId) return;
    try {
      localStorage.setItem(TENANT_KEY, tenantId);
    } catch {
      /* private mode */
    }
    get(`/t/${tenantId}`).then(setTenant).catch(() => setTenant(null));
  }, [tenantId]);

  useEffect(() => setNavOpen(false), [path]);

  // The emailed reset link works whoever is signed in, or nobody.
  if (path === "/reset") return html`<${ResetPage} />`;
  // Public purchase page: before sign-in checks, so visitors from the pricing page reach it directly.
  if (path === "/join" && me === null) return html`<${JoinPage} />`;
  if (me === undefined) return html`<${Loading} />`;
  if (!me) return html`<${AuthPage} onDone=${loadMe} />`;
  if (path === "/join") {
    navigate(IN_APP ? "/" : "/settings/billing", { replace: true });
    return html`<${Loading} />`;
  }
  if (path === "/admin" && me.user.is_platform_admin) {
    return html`<${Shell} me=${me} tenantId=${tenantId} setTenantId=${setTenantId} path=${path} navOpen=${navOpen} setNavOpen=${setNavOpen}><${AdminPage} onChange=${loadMe} /></${Shell}>`;
  }
  // RELAYZA's own reps and partners train here; they usually belong to no business at all.
  if ((path === "/academy" || path.startsWith("/academy/")) && me.academy) {
    return html`<${Shell} me=${me} tenantId=${tenantId} setTenantId=${setTenantId} path=${path} navOpen=${navOpen} setNavOpen=${setNavOpen}><${AcademyPage} path=${path} me=${me} /></${Shell}>`;
  }
  if (!tenantId && me.academy) {
    navigate("/academy", { replace: true });
    return html`<${Loading} />`;
  }
  if (!tenantId) return html`<div class="center-card"><h2>${tr("No business yet")}</h2><p class="muted">${tr("Ask your administrator to add you to a business.")}</p></div>`;
  if (!tenant || tenant.id !== tenantId) return html`<${Loading} />`;

  const t = { ...tenant, base: `/t/${tenant.id}`, reload: () => get(`/t/${tenant.id}`).then(setTenant) };
  // A business that hasn't been through guided setup lands in the wizard, not on an empty overview.
  if (path === "/" && tenant.setup && !tenant.setup.done && tenant.role !== "agent") {
    navigate("/setup", { replace: true });
    return html`<${Loading} />`;
  }
  let page;
  if (path === "/") page = html`<${OverviewPage} t=${t} />`;
  else if (path.startsWith("/inbox")) page = html`<${InboxPage} t=${t} selectedId=${path.split("/")[2] ?? null} me=${me} />`;
  else if (path === "/email") page = html`<${EmailPage} t=${t} />`;
  else if (path === "/calls") page = html`<${CallsPage} t=${t} />`;
  else if (path === "/bookings") page = html`<${BookingsPage} t=${t} />`;
  else if (path === "/contacts") page = html`<${ContactsPage} t=${t} />`;
  else if (path === "/tasks") page = html`<${TasksPage} t=${t} onChange=${() => window.dispatchEvent(new Event("rz:attention"))} />`;
  else if (path === "/services") page = html`<${ServicesPage} t=${t} />`;
  else if (path === "/setup") page = html`<${SetupPage} t=${t} />`;
  else if (path === "/agent") page = html`<${AgentPage} t=${t} me=${me} />`;
  else if (path === "/channels") page = html`<${ChannelsPage} t=${t} me=${me} />`;
  else if (path === "/campaigns") page = html`<${CampaignsPage} t=${t} />`;
  else if (path === "/intake") page = html`<${IntakePage} key=${t.id} t=${t} />`;
  else if (path === "/website") page = html`<${WebsitePage} t=${t} />`;
  else if (path.startsWith("/settings")) page = html`<${SettingsPage} t=${t} me=${me} />`;
  else page = html`<div class="empty"><strong>${tr("Page not found")}</strong><a href="/">${tr("Back to overview")}</a></div>`;

  return html`<${Shell} me=${me} t=${t} tenant=${tenant} tenantId=${tenantId} setTenantId=${setTenantId} path=${path} navOpen=${navOpen} setNavOpen=${setNavOpen}><${BillingBanner} t=${t} />${page}</${Shell}>`;
}

/**
 * The Academy link shows under RELAYZA's own business only. Someone who can't select that business (a
 * trainee with no business, or only their own) sees it wherever they are, or they'd have no way in.
 */
const academyHere = (me, tenantId) => !me.academy_tenant_id || tenantId === me.academy_tenant_id || !me.tenants.some((t) => t.id === me.academy_tenant_id);

/** Badge counts for the sidebar ("needs a person", tasks due). Refreshes every 30s and on "rz:attention". */
function useAttention(t) {
  const [data, reload] = useLoad(() => (t ? get(`${t.base}/attention`) : Promise.resolve(null)), [t?.id]);
  usePoll(useCallback(reload, [t?.id]), 30_000);
  useEffect(() => {
    window.addEventListener("rz:attention", reload);
    return () => window.removeEventListener("rz:attention", reload);
  }, [t?.id]);
  return data;
}

function Shell({ me, t, tenantId, setTenantId, path, navOpen, setNavOpen, children }) {
  const attention = useAttention(t);
  const assistant = useAssistant(t);
  const waiting = pendingCount(assistant.state);
  const logout = async () => {
    await post("/auth/logout");
    location.href = "/";
  };
  const [theme, setThemeState] = useState(getTheme);
  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    setThemeState(next);
  };
  const isActive = (p) => (p === "/" ? path === "/" : path.startsWith(p));
  // On mobile the open sidebar covers the ☰ button, so it needs its own ways out: Escape, a tap on
  // the backdrop, or any link in it (including the page you're already on, which doesn't change path).
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => e.key === "Escape" && setNavOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);
  const closeOnLink = (e) => e.target.closest?.("a") && setNavOpen(false);
  return html`<div class="shell ${navOpen ? "nav-open" : ""}" onClick=${linkHandler}>
    ${navOpen && html`<div class="nav-scrim" aria-hidden="true" onClick=${() => setNavOpen(false)}></div>`}
    <aside class="sidebar" onClick=${closeOnLink}>
      <${Logo} />
      ${!me.tenants.length
        ? ""
        : me.tenants.length > 1 || me.user.is_platform_admin
        ? html`<select class="tenant-switch" aria-label=${tr("Business")} value=${tenantId ?? ""} onChange=${(e) => { setTenantId(e.target.value); navigate("/"); }}>
            ${me.tenants.map((t) => html`<option value=${t.id}>${t.name}</option>`)}
          </select>`
        : html`<div class="tenant-name">${me.tenants[0]?.name}</div>`}
      ${t &&
      html`<button class="assistant-open ${assistant.open ? "active" : ""}" aria-expanded=${assistant.open} onClick=${() => { assistant.setOpen(!assistant.open); setNavOpen(false); }}>
        <${Sparkles} />${tr("Ask the assistant")}
        ${waiting ? html`<span class="nav-badge" aria-label=${tr("{n} waiting for you", { n: waiting })}>${waiting}</span>` : ""}
      </button>`}
      <nav>
        ${(me.tenants.length ? NAV : []).map((n) => {
          const count = attention && n.badge ? n.badge(attention) : 0;
          return html`<a href=${n.path} class=${isActive(n.path) ? "active" : ""}><span class="nav-icon" aria-hidden="true">${n.icon}</span>${tr(n.label)}${count ? html`<span class="nav-badge" aria-label=${tr("{n} need attention", { n: count })}>${count}</span>` : ""}</a>`;
        })}
        ${me.academy && academyHere(me, tenantId) && html`<a href="/academy" class=${path.startsWith("/academy") ? "active" : ""}><span class="nav-icon" aria-hidden="true">◆</span>${tr("Academy")}</a>`}
        ${me.user.is_platform_admin && html`<a href="/admin" class=${path === "/admin" ? "active" : ""}><span class="nav-icon" aria-hidden="true">★</span>${tr("All businesses")}</a>`}
      </nav>
      <div class="sidebar-foot">
        <div class="muted small">${me.user.name}<br />${me.user.email}</div>
        <div class="foot-actions">
          ${!IN_APP && html`<a class="link" href=${getLang() === "he" ? `${MARKETING_URL}/he` : MARKETING_URL} target="_blank" rel="noopener">${tr("RELAYZA website")}</a>`}
          ${IN_APP
            ? html`<a class="link" href="relayza://quick-view">${tr("Quick view")}</a>`
            : html`<a class="link" href="/download" target="_blank" rel="noopener">${tr("Mobile app")}</a>
                <button class="link" onClick=${logout}>${tr("Sign out")}</button>`}
          <button class="link" onClick=${toggleTheme}>${theme === "light" ? tr("Dark mode") : tr("Light mode")}</button>
          <${LangToggle} />
        </div>
      </div>
    </aside>
    <div class="main">
      <div class="topbar">
        <button class="icon-btn" aria-label=${tr("Menu")} aria-expanded=${navOpen} onClick=${() => setNavOpen(!navOpen)}>☰</button>
        <${Logo} />
        ${IN_APP && html`<a class="btn small quick-view" href="relayza://quick-view">${tr("Quick view")}</a>`}
        ${t && html`<button class="icon-btn assistant-top" aria-label=${tr("Ask the assistant")} onClick=${() => assistant.setOpen(!assistant.open)}><${Sparkles} />${waiting ? html`<span class="dot" aria-hidden="true"></span>` : ""}</button>`}
      </div>
      <main class="content">${children}</main>
    </div>
    ${t && html`<${AssistantDrawer} t=${t} path=${path} assistant=${assistant} />`}
    <${Toaster} />
  </div>`;
}

render(html`<${App} />`, document.getElementById("app"));

